import type { Language } from './languages';

type TranslationLeaf = { [L in Language]: string };
type TranslationNode = TranslationLeaf | { [key: string]: TranslationNode };

type I18nLang<T, L extends Language> = T extends TranslationLeaf
  ? T[L]
  : T extends Record<string, unknown>
  ? { [K in keyof T]: I18nLang<T[K], L> }
  : never;

export type I18nTranslations<T extends TranslationNode> = {
  [L in Language]: I18nLang<T, L>;
};

function isLeaf(node: unknown): node is Record<string, string> {
  return (
    typeof node === 'object' &&
    node !== null &&
    !Array.isArray(node) &&
    Object.values(node).every((v) => typeof v === 'string')
  );
}

function setDeep(target: Record<string, unknown>, path: string[], value: string) {
  let cur: Record<string, unknown> = target;
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i]!;
    if (cur[k] == null || typeof cur[k] !== 'object') cur[k] = {};
    cur = cur[k] as Record<string, unknown>;
  }
  cur[path[path.length - 1]!] = value;
}

export function toI18n<T extends TranslationNode>(dx: T): I18nTranslations<T> {
  const out: Record<string, unknown> = {};

  const walk = (node: TranslationNode, path: string[]): void => {
    if (isLeaf(node)) {
      // No asumimos idiomas: salen de las keys de cada hoja
      for (const [lang, text] of Object.entries(node)) {
        setDeep(out, [lang, ...path], text);
      }
      return;
    }

    for (const key of Object.keys(node)) {
      walk(node[key], [...path, key]);
    }
  };

  walk(dx, []);
  return out as I18nTranslations<T>;
}
